import { type AdminSubscription, formatUsPhone, queryKeys } from '@mevaser/shared'
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Skeleton } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Pencil, Plus, Smartphone, XCircle } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { SellPlanDialog } from './sell-plan-dialog.js'
import {
  AddSubscriptionDialog,
  CancelSubscriptionDialog,
  EditSubscriptionDialog,
  type PlanDraft,
  planPrice,
  profitText,
  renewalText,
  SubscriptionStatusBadge,
} from './subscription-dialogs.js'

/**
 * A customer's phone plans on their file (t1083): status, line and renewal
 * date, with edit and cancel for staff who can manage them. The Plans page
 * lists every customer's; this is the one customer's, in place.
 */
export function CustomerPlansCard({
  customerId,
  canWrite,
  canSell,
  taxExempt,
}: {
  customerId: string
  canWrite: boolean
  /** May take a payment (t1308): Add plan then sells the plan on a card on file. */
  canSell: boolean
  taxExempt: boolean
}) {
  const [editing, setEditing] = useState<AdminSubscription | null>(null)
  const [cancelling, setCancelling] = useState<AdminSubscription | null>(null)
  const [adding, setAdding] = useState<'sell' | 'record' | null>(null)
  // Details already typed in the sell dialog, carried into the record dialog.
  const [draft, setDraft] = useState<PlanDraft | null>(null)
  // A sold plan reaches Stripe a few seconds after the payment, so the list is
  // re-read for a short while instead of leaving "not billed by Stripe" up.
  const [watchUntil, setWatchUntil] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.subscriptions.list({ customerId }),
    queryFn: () =>
      api.get<{ items: AdminSubscription[] }>('/subscriptions', {
        query: { customerId, pageSize: 50 },
      }),
    refetchInterval: () => (Date.now() < watchUntil ? 3000 : false),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="size-4" aria-hidden="true" /> Plans
          {canWrite ? (
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setDraft(null)
                setAdding(canSell ? 'sell' : 'record')
              }}
            >
              <Plus /> Add plan
            </Button>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ) : data?.items.length ? (
          <ul className="divide-y divide-border">
            {data.items.map((sub) => (
              <li key={sub.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {sub.label ? `${sub.label} · ` : ''}
                    <span className={sub.label ? 'font-normal text-foreground-muted' : ''}>
                      {sub.planName}
                    </span>
                  </p>
                  <p className="text-xs text-foreground-muted">
                    {sub.provider ? `${sub.provider} · ` : ''}
                    {sub.phoneLine ? `${formatUsPhone(sub.phoneLine)} · ` : ''}
                    {planPrice(sub)}
                    {profitText(sub) ? ` · ${profitText(sub)}` : ''}
                  </p>
                  <p
                    className={`text-xs ${sub.status === 'past_due' ? 'font-medium text-danger' : 'text-foreground-muted'}`}
                  >
                    {renewalText(sub)}
                    {sub.billedByStripe ? ' · card on file' : ' · billed by hand'}
                  </p>
                </div>
                <SubscriptionStatusBadge sub={sub} />
                {canWrite ? (
                  <div className="flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${sub.label ?? sub.planName}`}
                      onClick={() => setEditing(sub)}
                    >
                      <Pencil />
                    </Button>
                    {sub.status !== 'cancelled' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Cancel ${sub.label ?? sub.planName}`}
                        onClick={() => setCancelling(sub)}
                      >
                        <XCircle />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Smartphone}
              title="No plans on record"
              description="A plan sold to them appears here. Use Add plan to sell one now, or to record service set up elsewhere."
            />
          </div>
        )}
      </CardContent>

      <SellPlanDialog
        customerId={customerId}
        taxExempt={taxExempt}
        open={adding === 'sell'}
        onClose={() => setAdding(null)}
        onRecordInstead={(d) => {
          setDraft(d)
          setAdding('record')
        }}
        onSold={() => setWatchUntil(Date.now() + 30_000)}
      />
      <AddSubscriptionDialog
        customerId={customerId}
        open={adding === 'record'}
        onClose={() => setAdding(null)}
        initial={draft}
      />
      <EditSubscriptionDialog sub={editing} onClose={() => setEditing(null)} />
      <CancelSubscriptionDialog sub={cancelling} onClose={() => setCancelling(null)} />
    </Card>
  )
}
